import { AuthFailure, useAuth } from '@/auth/auth-provider'
import { Alert, Button, Center, Loader, LoadingOverlay, Stack, Text } from '@mantine/core'
import { ReactNode, useEffect, useRef } from 'react'

interface ProtectedRouteProps {
  children: ReactNode
}

/** Signs the visitor in. What they may see once signed in is decided by role, here and on the API. */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, signIn, error } = useAuth()
  const redirected = useRef(false)

  // Send an unauthenticated visitor to sign in — but only once, and never when sign-in
  // has already failed. Retrying on failure turns one bad token into an endless bounce
  // between here and the identity provider, which is indistinguishable from a hang.
  useEffect(() => {
    if (isLoading || isAuthenticated || error || redirected.current) {
      return
    }
    redirected.current = true
    void signIn()
  }, [isAuthenticated, isLoading, error, signIn])

  // Something stopped a session being established. Say which, and offer the remedy that
  // matches it — re-authenticating does nothing for an API that is simply down.
  if (!isLoading && error) {
    const failure = (error as { failure?: AuthFailure }).failure
    const unreachable = failure === 'unreachable'

    return (
      <Center h="100vh" p="md">
        <Stack align="center" gap="md" maw={520}>
          <Alert
            color={unreachable ? 'yellow' : 'red'}
            title={unreachable ? 'Cannot reach the API' : failure === 'no-role' ? 'No dashboard access' : 'Cannot sign you in'}
            w="100%"
          >
            {error.message}
          </Alert>
          <Button
            variant="light"
            onClick={() => {
              if (unreachable) {
                // Nothing wrong with the session — just load the page again once the API is back.
                window.location.reload()
                return
              }
              redirected.current = false
              void signIn()
            }}
          >
            {unreachable ? 'Retry' : 'Try again'}
          </Button>
        </Stack>
      </Center>
    )
  }

  if (isLoading) {
    return (
      <LoadingOverlay visible>
        <Center h="100vh">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text size="sm" c="dimmed">
              Authenticating...
            </Text>
          </Stack>
        </Center>
      </LoadingOverlay>
    )
  }

  if (!isAuthenticated) {
    // This will show briefly before redirect
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    )
  }

  return <>{children}</>
}
